"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale } from "@/lib/i18n";
import { useState, useEffect } from "react";
import { useParams } from "next/navigation";
import { Link } from "@/i18n/navigation";
import { useBetaPrefix } from "@/lib/use-lang-prefix";
import { useAuth } from "@/app/contexts/AuthContext";
import { useToast } from "@/app/components/Toast";
import { authHeaders } from "@/app/[locale]/admin/shared";
import { cachedFetch } from "@/lib/fetch-cache";
import RunSummary, { type PotionInfo } from "./RunSummary";
import SimilarRuns from "./SimilarRuns";
import { CardPill, RelicPill, cleanId, displayName, type CardInfo, type RelicInfo } from "./RunPills";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
const CHAR_CSS_VAR: Record<string, string> = {
  IRONCLAD: "var(--color-ironclad)",
  SILENT: "var(--color-silent)",
  DEFECT: "var(--color-defect)",
  NECROBINDER: "var(--color-necrobinder)",
  REGENT: "var(--color-regent)"
};

// `initialRun` is the server-fetched run from page.tsx. With it, the whole
// page server-renders with real data — before, the body was an empty shell
// until the client refetched the same endpoint, so every run page looked
// identical to crawlers (duplicate content) and carried no unique text.
export default function SharedRunClient(t0) {
  const $ = _c(225);
  const {
    initialRun
  } = t0;
  const {
    hash
  } = useParams();
  const bp = useBetaPrefix();
  const lang = useGameLocale();
  const t = useT();
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const [run, setRun] = useState(initialRun ?? null);
  const [loading, setLoading] = useState(!initialRun);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportEmail, setReportEmail] = useState("");
  const [reportReason, setReportReason] = useState("");
  const [reportState, setReportState] = useState("idle");
  const [reportError, setReportError] = useState(null);
  const [unhiding, setUnhiding] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [cardData, setCardData] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [relicData, setRelicData] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [potionData, setPotionData] = useState(t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [charNames, setCharNames] = useState(t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {};
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const [encounterNames, setEncounterNames] = useState(t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {};
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const [actNames, setActNames] = useState(t6);
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {};
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const [monsterNames, setMonsterNames] = useState(t7);
  const [showDetails, setShowDetails] = useState(false);
  let t8;
  if ($[7] !== hash || $[8] !== initialRun || $[9] !== lang) {
    t8 = () => {
      if (!hash) {
        return;
      }
      if (!initialRun) {
        fetch(`${API}/api/runs/shared/${hash}`).then(_temp).then(setRun).catch(() => setNotFound(true)).finally(() => setLoading(false));
      }
      cachedFetch(`${API}/api/cards?lang=${lang}`).then(cards => {
        const m = {};
        for (const c of cards) {
          m[c.id] = c;
        }
        setCardData(m);
      });
      cachedFetch(`${API}/api/relics?lang=${lang}`).then(relics => {
        const m_0 = {};
        for (const r_0 of relics) {
          m_0[r_0.id] = r_0;
        }
        setRelicData(m_0);
      });
      cachedFetch(`${API}/api/potions?lang=${lang}`).then(potions => {
        const m_1 = {};
        for (const p of potions) {
          m_1[p.id] = p;
        }
        setPotionData(m_1);
      });
      cachedFetch(`${API}/api/characters?lang=${lang}`).then(chars => {
        const m_2 = {};
        for (const c_0 of chars) {
          m_2[c_0.id.toUpperCase()] = c_0.name;
        }
        setCharNames(m_2);
      });
      cachedFetch(`${API}/api/encounters?lang=${lang}`).then(encs => {
        const m_3 = {};
        for (const e of encs) {
          m_3[e.id.toUpperCase()] = e.name;
        }
        setEncounterNames(m_3);
      });
      cachedFetch(`${API}/api/acts?lang=${lang}`).then(acts => {
        const m_4 = {};
        for (const a of acts) {
          m_4[a.id.toUpperCase()] = a.name;
        }
        setActNames(m_4);
      });
      cachedFetch(`${API}/api/monsters?lang=${lang}`).then(monsters => {
        const m_5 = {};
        for (const mo of monsters) {
          m_5[mo.id.toUpperCase()] = mo.name;
        }
        setMonsterNames(m_5);
      });
    };
    $[7] = hash;
    $[8] = initialRun;
    $[9] = lang;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== hash || $[12] !== lang) {
    t9 = [hash, lang];
    $[11] = hash;
    $[12] = lang;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  useEffect(t8, t9);
  let t10;
  if ($[14] !== lang || $[15] !== run?.is_beta) {
    t10 = () => {
      if (!run?.is_beta) {
        return;
      }
      let cancelled = false;
      cachedFetch(`${API}/api/cards?lang=${lang}&channel=beta`).then(cards_0 => {
        if (cancelled) {
          return;
        }
        setCardData(prev => {
          const m_6 = {
            ...prev
          };
          for (const c_1 of cards_0) {
            if (!(c_1.id in m_6)) {
              m_6[c_1.id] = c_1;
            }
          }
          return m_6;
        });
      });
      cachedFetch(`${API}/api/relics?lang=${lang}&channel=beta`).then(relics_0 => {
        if (cancelled) {
          return;
        }
        setRelicData(prev_0 => {
          const m_7 = {
            ...prev_0
          };
          for (const r_1 of relics_0) {
            if (!(r_1.id in m_7)) {
              m_7[r_1.id] = r_1;
            }
          }
          return m_7;
        });
      });
      cachedFetch(`${API}/api/potions?lang=${lang}&channel=beta`).then(potions_0 => {
        if (cancelled) {
          return;
        }
        setPotionData(prev_1 => {
          const m_8 = {
            ...prev_1
          };
          for (const p_0 of potions_0) {
            if (!(p_0.id in m_8)) {
              m_8[p_0.id] = p_0;
            }
          }
          return m_8;
        });
      });
      cachedFetch(`${API}/api/encounters?lang=${lang}&channel=beta`).then(encs_0 => {
        if (cancelled) {
          return;
        }
        setEncounterNames(prev_2 => {
          const m_9 = {
            ...prev_2
          };
          for (const e_0 of encs_0) {
            const k = e_0.id.toUpperCase();
            if (!(k in m_9)) {
              m_9[k] = e_0.name;
            }
          }
          return m_9;
        });
      });
      return () => {
        cancelled = true;
      };
    };
    $[14] = lang;
    $[15] = run?.is_beta;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = run?.is_beta;
  let t12;
  if ($[17] !== lang || $[18] !== t11) {
    t12 = [t11, lang];
    $[17] = lang;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  useEffect(t10, t12);
  let t13;
  if ($[20] !== charNames) {
    t13 = function localizedCharName(id) {
      const key = cleanId(id).toUpperCase();
      return charNames[key] ?? displayName(id);
    };
    $[20] = charNames;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const localizedCharName = t13;
  let t14;
  if ($[22] !== encounterNames) {
    t14 = function localizedEncounterName(id_0) {
      const key_0 = cleanId(id_0).toUpperCase();
      return encounterNames[key_0] ?? displayName(id_0);
    };
    $[22] = encounterNames;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const localizedEncounterName = t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = function copyLink() {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const copyLink = t15;
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== reportEmail) {
    t17 = t16.test(reportEmail.trim());
    $[26] = reportEmail;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const emailOk = t17;
  let t18;
  if ($[28] !== emailOk || $[29] !== hash || $[30] !== reportEmail || $[31] !== reportReason) {
    t18 = function submitReport() {
      if (!emailOk || !reportReason.trim()) {
        return;
      }
      setReportState("sending");
      setReportError(null);
      fetch(`${API}/api/feedback/run-report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          run_hash: hash,
          email: reportEmail.trim(),
          reason: reportReason.trim()
        })
      }).then(async r_2 => {
        if (!r_2.ok) {
          const body = await r_2.json().catch(_temp2);
          throw new Error(body?.detail || `${r_2.status}`);
        }
        setReportState("sent");
        setTimeout(() => {
          setShowReport(false);
          setReportState("idle");
          setReportReason("");
          setReportEmail("");
        }, 1800);
      }).catch(e_1 => {
        setReportState("error");
        setReportError(String((e_1 as Error)?.message || e_1));
      });
    };
    $[28] = emailOk;
    $[29] = hash;
    $[30] = reportEmail;
    $[31] = reportReason;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const submitReport = t18;
  let t19;
  if ($[33] !== hash || $[34] !== run || $[35] !== t || $[36] !== toast) {
    t19 = function setHidden(hidden) {
      setUnhiding(true);
      fetch(`${API}/api/runs/${hash}/hidden`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders()
        },
        body: JSON.stringify({
          hidden
        })
      }).then(r_3 => {
        if (!r_3.ok) {
          throw new Error();
        }
        setRun({
          ...run,
          hidden
        });
        toast(hidden ? t("Run hidden") : t("Run unhidden"), "success");
      }).catch(() => toast(t("Failed to update this run"), "error")).finally(() => setUnhiding(false));
    };
    $[33] = hash;
    $[34] = run;
    $[35] = t;
    $[36] = toast;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const setHidden = t19;
  if (loading) {
    let t20;
    if ($[38] !== t) {
      t20 = t("Loading...");
      $[38] = t;
      $[39] = t20;
    } else {
      t20 = $[39];
    }
    let t21;
    if ($[40] !== t20) {
      t21 = <div className="max-w-4xl mx-auto px-4 py-12 text-center text-[var(--text-muted)]">{t20}</div>;
      $[40] = t20;
      $[41] = t21;
    } else {
      t21 = $[41];
    }
    return t21;
  }
  if (notFound || !run) {
    let t20;
    if ($[42] !== t) {
      t20 = t("Run not found.");
      $[42] = t;
      $[43] = t20;
    } else {
      t20 = $[43];
    }
    let t21;
    if ($[44] !== t20) {
      t21 = <p className="text-[var(--text-muted)] mb-4">{t20}</p>;
      $[44] = t20;
      $[45] = t21;
    } else {
      t21 = $[45];
    }
    const t22 = `${bp}/leaderboards`;
    let t23;
    if ($[46] !== t) {
      t23 = t("Back to");
      $[46] = t;
      $[47] = t23;
    } else {
      t23 = $[47];
    }
    let t24;
    if ($[48] !== t22 || $[49] !== t23) {
      t24 = <Link href={t22} className="text-[var(--accent-gold)] hover:underline">← {t23}</Link>;
      $[48] = t22;
      $[49] = t23;
      $[50] = t24;
    } else {
      t24 = $[50];
    }
    let t25;
    if ($[51] !== t21 || $[52] !== t24) {
      t25 = <div className="max-w-4xl mx-auto px-4 py-12 text-center">{t21}{t24}</div>;
      $[51] = t21;
      $[52] = t24;
      $[53] = t25;
    } else {
      t25 = $[53];
    }
    return t25;
  }
  const player = run.players[run.player_index ?? 0] ?? run.players[0];
  let T0;
  let charColor;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  if ($[54] !== bp || $[55] !== copied || $[56] !== emailOk || $[57] !== hash || $[58] !== player.character || $[59] !== reportEmail || $[60] !== reportError || $[61] !== reportReason || $[62] !== reportState || $[63] !== run.has_replay || $[64] !== run.hidden || $[65] !== run.map_point_history || $[66] !== run.was_abandoned || $[67] !== run.win || $[68] !== setHidden || $[69] !== showReport || $[70] !== submitReport || $[71] !== t || $[72] !== unhiding || $[73] !== user?.is_admin) {
    const charId = cleanId(player.character);
    charColor = CHAR_CSS_VAR[charId.toUpperCase()] || "var(--accent-gold)";
    run.map_point_history?.reduce(_temp3, 0) || 0;
    t26 = "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8";
    const t30 = `${bp}/leaderboards`;
    let t31;
    if ($[86] !== t) {
      t31 = t("Back to");
      $[86] = t;
      $[87] = t31;
    } else {
      t31 = $[87];
    }
    let t32;
    if ($[88] !== t30 || $[89] !== t31) {
      t32 = <Link href={t30} className="text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">← {t31}</Link>;
      $[88] = t30;
      $[89] = t31;
      $[90] = t32;
    } else {
      t32 = $[90];
    }
    let t33;
    if ($[91] !== hash || $[92] !== run.has_replay || $[93] !== t) {
      t33 = run.has_replay && <Link href={`/runs/${hash}/replay`} className="text-xs px-3 py-1.5 rounded-lg border border-accent text-accent hover:bg-accent/10 transition-colors">{t("Replay")}</Link>;
      $[91] = hash;
      $[92] = run.has_replay;
      $[93] = t;
      $[94] = t33;
    } else {
      t33 = $[94];
    }
    let t34;
    if ($[95] !== run.hidden || $[96] !== setHidden || $[97] !== t || $[98] !== unhiding || $[99] !== user?.is_admin) {
      t34 = user?.is_admin && <button onClick={() => setHidden(!run.hidden)} disabled={unhiding} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--accent-gold)]/40 text-[var(--accent-gold)] hover:border-[var(--accent-gold)] transition-colors disabled:opacity-50">{unhiding ? "..." : run.hidden ? t("Unhide") : t("Hide")}</button>;
      $[95] = run.hidden;
      $[96] = setHidden;
      $[97] = t;
      $[98] = unhiding;
      $[99] = user?.is_admin;
      $[100] = t34;
    } else {
      t34 = $[100];
    }
    let t35;
    if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = () => setShowReport(true);
      $[101] = t35;
    } else {
      t35 = $[101];
    }
    let t36;
    if ($[102] !== t) {
      t36 = t("Report");
      $[102] = t;
      $[103] = t36;
    } else {
      t36 = $[103];
    }
    let t37;
    if ($[104] !== t36) {
      t37 = <button onClick={t35} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors">{t36}</button>;
      $[104] = t36;
      $[105] = t37;
    } else {
      t37 = $[105];
    }
    let t38;
    if ($[106] !== copied || $[107] !== t) {
      t38 = copied ? t("Copied!") : t("Share");
      $[106] = copied;
      $[107] = t;
      $[108] = t38;
    } else {
      t38 = $[108];
    }
    let t39;
    if ($[109] !== t38) {
      t39 = <button onClick={copyLink} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors">{t38}</button>;
      $[109] = t38;
      $[110] = t39;
    } else {
      t39 = $[110];
    }
    let t40;
    if ($[111] !== t33 || $[112] !== t34 || $[113] !== t37 || $[114] !== t39) {
      t40 = <div className="flex items-center gap-2">{t33}{t34}{t37}{t39}</div>;
      $[111] = t33;
      $[112] = t34;
      $[113] = t37;
      $[114] = t39;
      $[115] = t40;
    } else {
      t40 = $[115];
    }
    if ($[116] !== t32 || $[117] !== t40) {
      t27 = <div className="flex items-center justify-between mb-4">{t32}{t40}</div>;
      $[116] = t32;
      $[117] = t40;
      $[118] = t27;
    } else {
      t27 = $[118];
    }
    if ($[119] !== emailOk || $[120] !== reportEmail || $[121] !== reportError || $[122] !== reportReason || $[123] !== reportState || $[124] !== showReport || $[125] !== submitReport || $[126] !== t) {
      t28 = showReport && <div className="fixed inset-0 z-50 bg-scrim/60 flex items-center justify-center p-4" onClick={() => reportState !== "sending" && setShowReport(false)}><div className="bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-xl p-5 w-full max-w-md" onClick={_temp4}><h2 className="text-lg font-bold text-[var(--text-primary)] mb-3">{t("Report run")}</h2>{reportState === "sent" ? <p className="text-sm text-[var(--color-silent)] py-4">{t("Report sent. Thanks!")}</p> : <div className="space-y-3"><label className="block"><span className="text-xs text-[var(--text-muted)]">{t("Why are you reporting this run?")}</span><textarea value={reportReason} onChange={e_3 => setReportReason(e_3.target.value)} rows={4} maxLength={2000} className="mt-1 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--border-accent)] focus:outline-none" /></label><label className="block"><span className="text-xs text-[var(--text-muted)]">{t("Email")}</span><input type="email" value={reportEmail} onChange={e_4 => setReportEmail(e_4.target.value)} required={true} maxLength={254} className="mt-1 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--border-accent)] focus:outline-none" />{reportEmail.trim() !== "" && !emailOk && <span className="text-xs text-[var(--color-ironclad)]">{t("Enter a valid email address.")}</span>}</label>{reportState === "error" && <p className="text-xs text-[var(--color-ironclad)]">{reportError || t("Could not send the report.")}</p>}<div className="flex justify-end gap-2 pt-1"><button onClick={() => setShowReport(false)} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">{t("Cancel")}</button><button onClick={submitReport} disabled={reportState === "sending" || !emailOk || !reportReason.trim()} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--accent-gold)]/40 bg-[var(--accent-gold)]/10 text-[var(--accent-gold)] hover:border-[var(--accent-gold)] transition-colors disabled:opacity-50">{reportState === "sending" ? "..." : t("Send report")}</button></div></div>}</div></div>;
      $[119] = emailOk;
      $[120] = reportEmail;
      $[121] = reportError;
      $[122] = reportReason;
      $[123] = reportState;
      $[124] = showReport;
      $[125] = submitReport;
      $[126] = t;
      $[127] = t28;
    } else {
      t28 = $[127];
    }
    if ($[128] !== run.hidden || $[129] !== t) {
      t29 = run.hidden && <div className="mb-4 rounded-lg border border-[var(--accent-gold)]/40 bg-[var(--accent-gold)]/10 px-4 py-3 text-sm text-[var(--text-secondary)]">{t("This run doesn't count toward leaderboards or community stats.")}{" "}<a href="https://discord.gg/xMsTBeh" target="_blank" rel="noopener noreferrer" className="text-[var(--accent-gold)] hover:underline">{t("Reach out on Discord if that seems wrong.")}</a></div>;
      $[128] = run.hidden;
      $[129] = t;
      $[130] = t29;
    } else {
      t29 = $[130];
    }
    t24 = "rounded-xl border px-4 py-3 mb-4 flex items-center justify-between flex-wrap gap-2";
    const t41 = `color-mix(in srgb, ${charColor} 40%, transparent)`;
    const t42 = `color-mix(in srgb, ${charColor} 8%, var(--bg-card))`;
    if ($[131] !== t41 || $[132] !== t42) {
      t25 = {
        borderColor: t41,
        background: t42
      };
      $[131] = t41;
      $[132] = t42;
      $[133] = t25;
    } else {
      t25 = $[133];
    }
    t22 = "flex items-center gap-3 text-xl font-bold";
    const t43 = run.win ? "var(--color-silent)" : run.was_abandoned ? "var(--text-muted)" : "var(--color-ironclad)";
    let t44;
    if ($[134] !== t43) {
      t44 = {
        color: t43
      };
      $[134] = t43;
      $[135] = t44;
    } else {
      t44 = $[135];
    }
    let t45;
    if ($[136] !== run.was_abandoned || $[137] !== run.win || $[138] !== t) {
      t45 = run.win ? t("Victory") : run.was_abandoned ? t("Abandoned") : t("Defeat");
      $[136] = run.was_abandoned;
      $[137] = run.win;
      $[138] = t;
      $[139] = t45;
    } else {
      t45 = $[139];
    }
    if ($[140] !== t44 || $[141] !== t45) {
      t23 = <span style={t44}>{t45}</span>;
      $[140] = t44;
      $[141] = t45;
      $[142] = t23;
    } else {
      t23 = $[142];
    }
    T0 = Link;
    t20 = bp;
    t21 = charId.toLowerCase();
    $[54] = bp;
    $[55] = copied;
    $[56] = emailOk;
    $[57] = hash;
    $[58] = player.character;
    $[59] = reportEmail;
    $[60] = reportError;
    $[61] = reportReason;
    $[62] = reportState;
    $[63] = run.has_replay;
    $[64] = run.hidden;
    $[65] = run.map_point_history;
    $[66] = run.was_abandoned;
    $[67] = run.win;
    $[68] = setHidden;
    $[69] = showReport;
    $[70] = submitReport;
    $[71] = t;
    $[72] = unhiding;
    $[73] = user?.is_admin;
    $[74] = T0;
    $[75] = charColor;
    $[76] = t20;
    $[77] = t21;
    $[78] = t22;
    $[79] = t23;
    $[80] = t24;
    $[81] = t25;
    $[82] = t26;
    $[83] = t27;
    $[84] = t28;
    $[85] = t29;
  } else {
    T0 = $[74];
    charColor = $[75];
    t20 = $[76];
    t21 = $[77];
    t22 = $[78];
    t23 = $[79];
    t24 = $[80];
    t25 = $[81];
    t26 = $[82];
    t27 = $[83];
    t28 = $[84];
    t29 = $[85];
  }
  const t30 = `${t20}/characters/${t21}`;
  let t31;
  if ($[143] !== charColor) {
    t31 = {
      color: charColor
    };
    $[143] = charColor;
    $[144] = t31;
  } else {
    t31 = $[144];
  }
  let t32;
  if ($[145] !== localizedCharName || $[146] !== player.character) {
    t32 = localizedCharName(player.character);
    $[145] = localizedCharName;
    $[146] = player.character;
    $[147] = t32;
  } else {
    t32 = $[147];
  }
  let t33;
  if ($[148] !== T0 || $[149] !== t30 || $[150] !== t31 || $[151] !== t32) {
    t33 = <T0 href={t30} className="text-base font-normal hover:underline" style={t31}>{t32}</T0>;
    $[148] = T0;
    $[149] = t30;
    $[150] = t31;
    $[151] = t32;
    $[152] = t33;
  } else {
    t33 = $[152];
  }
  let t34;
  if ($[153] !== t22 || $[154] !== t23 || $[155] !== t33) {
    t34 = <h1 className={t22}>{t23}{t33}</h1>;
    $[153] = t22;
    $[154] = t23;
    $[155] = t33;
    $[156] = t34;
  } else {
    t34 = $[156];
  }
  let t35;
  if ($[157] !== t) {
    t35 = t("Ascension");
    $[157] = t;
    $[158] = t35;
  } else {
    t35 = $[158];
  }
  const t36 = run.ascension || 0;
  let t37;
  if ($[159] !== bp || $[160] !== localizedEncounterName || $[161] !== run.killed_by_encounter || $[162] !== run.was_abandoned || $[163] !== run.win || $[164] !== t) {
    t37 = !run.win && !run.was_abandoned && run.killed_by_encounter && run.killed_by_encounter !== "NONE.NONE" && <>{" \xB7 "}{t("Killed by")}{" "}<Link href={`${bp}/encounters/${cleanId(run.killed_by_encounter).toLowerCase()}`} className="hover:underline" style={{
        color: "var(--color-ironclad)"
      }}>{localizedEncounterName(run.killed_by_encounter)}</Link></>;
    $[159] = bp;
    $[160] = localizedEncounterName;
    $[161] = run.killed_by_encounter;
    $[162] = run.was_abandoned;
    $[163] = run.win;
    $[164] = t;
    $[165] = t37;
  } else {
    t37 = $[165];
  }
  let t38;
  if ($[166] !== t35 || $[167] !== t36 || $[168] !== t37) {
    t38 = <div className="text-sm text-[var(--text-muted)]">{t35} {t36}{t37}</div>;
    $[166] = t35;
    $[167] = t36;
    $[168] = t37;
    $[169] = t38;
  } else {
    t38 = $[169];
  }
  let t39;
  if ($[170] !== t24 || $[171] !== t25 || $[172] !== t34 || $[173] !== t38) {
    t39 = <div className={t24} style={t25}>{t34}{t38}</div>;
    $[170] = t24;
    $[171] = t25;
    $[172] = t34;
    $[173] = t38;
    $[174] = t39;
  } else {
    t39 = $[174];
  }
  let t40;
  if ($[175] !== localizedCharName || $[176] !== player.character) {
    t40 = localizedCharName(player.character);
    $[175] = localizedCharName;
    $[176] = player.character;
    $[177] = t40;
  } else {
    t40 = $[177];
  }
  let t41;
  if ($[178] !== localizedEncounterName || $[179] !== run.killed_by_encounter) {
    t41 = run.killed_by_encounter && run.killed_by_encounter !== "NONE.NONE" ? localizedEncounterName(run.killed_by_encounter) : undefined;
    $[178] = localizedEncounterName;
    $[179] = run.killed_by_encounter;
    $[180] = t41;
  } else {
    t41 = $[180];
  }
  let t42;
  if ($[181] !== actNames || $[182] !== bp || $[183] !== cardData || $[184] !== charColor || $[185] !== monsterNames || $[186] !== player || $[187] !== potionData || $[188] !== relicData || $[189] !== run || $[190] !== t40 || $[191] !== t41) {
    t42 = <RunSummary run={run} player={player} cardData={cardData} relicData={relicData} potionData={potionData} charColor={charColor} langPrefix={bp} charName={t40} encounterName={t41} actNames={actNames} monsterNames={monsterNames} />;
    $[181] = actNames;
    $[182] = bp;
    $[183] = cardData;
    $[184] = charColor;
    $[185] = monsterNames;
    $[186] = player;
    $[187] = potionData;
    $[188] = relicData;
    $[189] = run;
    $[190] = t40;
    $[191] = t41;
    $[192] = t42;
  } else {
    t42 = $[192];
  }
  let t43;
  if ($[193] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = () => setShowDetails(_temp5);
    $[193] = t43;
  } else {
    t43 = $[193];
  }
  const t44 = `inline-block transition-transform ${showDetails ? "rotate-90" : ""}`;
  let t45;
  if ($[194] !== t44) {
    t45 = <span className={t44}>{">"}</span>;
    $[194] = t44;
    $[195] = t45;
  } else {
    t45 = $[195];
  }
  let t46;
  if ($[196] !== showDetails || $[197] !== t) {
    t46 = showDetails ? t("Hide") : t("Show");
    $[196] = showDetails;
    $[197] = t;
    $[198] = t46;
  } else {
    t46 = $[198];
  }
  let t47;
  if ($[199] !== t) {
    t47 = t("detailed history");
    $[199] = t;
    $[200] = t47;
  } else {
    t47 = $[200];
  }
  let t48;
  if ($[201] !== t45 || $[202] !== t46 || $[203] !== t47) {
    t48 = <button onClick={t43} className="w-full text-left text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors mb-3 flex items-center gap-2">{t45}{t46} {t47}</button>;
    $[201] = t45;
    $[202] = t46;
    $[203] = t47;
    $[204] = t48;
  } else {
    t48 = $[204];
  }
  let t49;
  if ($[205] !== bp || $[206] !== cardData || $[207] !== hash || $[208] !== player.deck || $[209] !== player.relics || $[210] !== relicData || $[211] !== run.acts || $[212] !== run.map_point_history || $[213] !== showDetails || $[214] !== t) {
    t49 = showDetails && <><div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-subtle)] p-5 mb-4"><h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">{t("Final Deck")} ({player.deck.length})</h2><div className="flex flex-wrap gap-1.5">{player.deck.sort(_temp6).map((card, i) => {
            const cid = cleanId(card.id);
            return <CardPill key={`${cid}-${i}`} cardId={cid} upgraded={!!card.current_upgrade_level} enchantment={card.enchantment ? cleanId(card.enchantment.id) : undefined} cardData={cardData} bp={bp} className={`text-xs px-2 py-1 rounded border transition-colors hover:bg-[var(--bg-card-hover)] ${card.current_upgrade_level ? "border-[var(--color-silent)]/30 bg-[var(--color-silent)]/10 text-[var(--color-silent)]" : "bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-secondary)]"}`} />;
          })}</div></div><div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-subtle)] p-5 mb-4"><h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">{t("Relics")} ({player.relics.length})</h2><div className="flex flex-wrap gap-1.5">{player.relics.map((relic, i_0) => {
            const rid = cleanId(relic.id);
            return <RelicPill key={`${rid}-${i_0}`} relicId={rid} relicData={relicData} bp={bp} className="text-xs px-2 py-1 rounded bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--accent-gold)] hover:bg-[var(--bg-card-hover)] transition-colors">{displayName(relic.id)}<span className="text-[var(--text-muted)] ml-1">F{relic.floor_added_to_deck}</span></RelicPill>;
          })}</div></div><div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-subtle)] p-5"><h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">{t("Floor History")}</h2><div className="space-y-1">{run.map_point_history?.map((actFloors, actIdx) => <div key={actIdx}><h3 className="text-sm font-semibold text-[var(--text-muted)] uppercase tracking-wider mt-3 mb-1.5">{displayName(run.acts?.[actIdx] || `Act ${actIdx + 1}`)}</h3>{actFloors.map(_temp11)}</div>)}</div></div><SimilarRuns hash={hash} /></>;
    $[205] = bp;
    $[206] = cardData;
    $[207] = hash;
    $[208] = player.deck;
    $[209] = player.relics;
    $[210] = relicData;
    $[211] = run.acts;
    $[212] = run.map_point_history;
    $[213] = showDetails;
    $[214] = t;
    $[215] = t49;
  } else {
    t49 = $[215];
  }
  let t50;
  if ($[216] !== t26 || $[217] !== t27 || $[218] !== t28 || $[219] !== t29 || $[220] !== t39 || $[221] !== t42 || $[222] !== t48 || $[223] !== t49) {
    t50 = <div className={t26}>{t27}{t28}{t29}{t39}{t42}{t48}{t49}</div>;
    $[216] = t26;
    $[217] = t27;
    $[218] = t28;
    $[219] = t29;
    $[220] = t39;
    $[221] = t42;
    $[222] = t48;
    $[223] = t49;
    $[224] = t50;
  } else {
    t50 = $[224];
  }
  return t50;
}
function _temp11(floor, floorIdx) {
  const ps = floor.player_stats?.[0];
  const room = floor.rooms?.[0];
  const encounter = room?.model_id ? displayName(room.model_id) : floor.map_point_type;
  const roomColors = {
    monster: "var(--text-secondary)",
    elite: "var(--accent-gold)",
    boss: "var(--color-ironclad)",
    rest: "var(--color-silent)",
    shop: "var(--accent-teal)",
    event: "var(--color-necrobinder)",
    treasure: "var(--accent-gold)"
  };
  const picked = ps?.card_choices?.filter(_temp7).map(_temp8) || [];
  const skipped = ps?.card_choices?.filter(_temp9).map(_temp10) || [];
  return <div key={floorIdx} className="flex items-start gap-3 py-1.5 border-b border-[var(--border-subtle)] last:border-0 text-xs"><span className="text-[var(--text-muted)] w-6 text-right flex-shrink-0">{floorIdx + 1}</span><span className="w-14 flex-shrink-0 font-medium" style={{
      color: roomColors[floor.map_point_type] || "var(--text-secondary)"
    }}>{floor.map_point_type}</span><div className="flex-1 min-w-0"><span className="text-[var(--text-secondary)]">{encounter}</span>{room?.turns_taken != null && <span className="text-[var(--text-muted)] ml-1">({room.turns_taken}T)</span>}{picked.length > 0 && <span className="ml-2" style={{
        color: "var(--color-silent)"
      }}>+{picked.join(", ")}</span>}{skipped.length > 0 && <span className="text-[var(--text-muted)] ml-1 line-through">{skipped.join(", ")}</span>}</div>{ps && <div className="flex items-center gap-2 flex-shrink-0 text-[var(--text-muted)]">{ps.damage_taken > 0 && <span style={{
        color: "var(--color-ironclad)"
      }}>-{ps.damage_taken}</span>}{ps.hp_healed > 0 && <span style={{
        color: "var(--color-silent)"
      }}>+{ps.hp_healed}</span>}<span>{ps.current_hp}/{ps.max_hp}</span></div>}</div>;
}
function _temp10(c_5) {
  return displayName(c_5.card.id);
}
function _temp9(c_4) {
  return !c_4.was_picked;
}
function _temp8(c_3) {
  return displayName(c_3.card.id);
}
function _temp7(c_2) {
  return c_2.was_picked;
}
function _temp6(a_0, b) {
  return cleanId(a_0.id).localeCompare(cleanId(b.id));
}
function _temp5(v) {
  return !v;
}
function _temp4(e_2) {
  return e_2.stopPropagation();
}
function _temp3(sum, act) {
  return sum + act.length;
}
function _temp2() {
  return null;
}
function _temp(r) {
  if (!r.ok) {
    throw new Error();
  }
  return r.json();
}